// Layouts/Assignments/StudentSubmissionUpload.tsx
import React, { useRef, useState } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import type { Submission } from '../ViewAssignment';

/* ─── Design tokens ──────────────────────────────────────────────── */
const C = {
  navy:        '#0e225d',
  navyHover:   'rgba(14,34,93,0.06)',
  gold:        '#fbb21f',
  goldDim:     'rgba(251,178,31,0.18)',
  goldBorder:  'rgba(251,178,31,0.35)',
  white:       '#ffffff',
  border:      '#e5e7eb',
  muted:       '#9ca3af',
  sub:         '#6b7280',
  body:        '#1f2937',
  green:       '#16a34a',
  greenDim:    'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red:         '#dc2626',
  redDim:      'rgba(220,38,38,0.08)',
  redBorder:   'rgba(220,38,38,0.25)',
  amber:       '#f59e0b',
  amberDim:    'rgba(245,158,11,0.12)',
  amberBorder: 'rgba(245,158,11,0.35)',
  blue:        '#3b82f6',
  blueDim:     'rgba(59,130,246,0.10)',
  blueBorder:  'rgba(59,130,246,0.30)',
};

/* ─── Types ──────────────────────────────────────────────────────── */
interface AttachedFile {
  id:   string;
  name: string;
  size: number;
  type: string;
  file: File;
}

interface Props {
  assignmentId:         string;
  assignmentTitle:      string;
  submissionDeadline:   string | null;
  allowLateSubmissions: boolean;
  existingSubmission:   Submission | null | undefined;
  onSubmissionUpdate:   () => void;
  toast: React.RefObject<Toast | null>;
}

/* ─── Helpers ────────────────────────────────────────────────────── */
const formatFileSize = (bytes: number) => {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (d: string | null | undefined) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg', 'image/jpg', 'image/png',
  'application/zip', 'application/x-rar-compressed',
].join(',');

/* ─── Sub-components ─────────────────────────────────────────────── */
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: '0.72rem', fontWeight: '700', color: C.navy, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
    {children}
  </div>
);

/* ─── Component ──────────────────────────────────────────────────── */
const StudentSubmissionUpload: React.FC<Props> = ({
  assignmentId,
  submissionDeadline,
  allowLateSubmissions,
  existingSubmission,
  onSubmissionUpdate,
  toast,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [submissionText, setSubmissionText] = useState(existingSubmission?.submission_text ?? '');
  const [attachedFiles,  setAttachedFiles]  = useState<AttachedFile[]>([]);
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [confirmOpen,    setConfirmOpen]    = useState(false);

  /* ── deadline checks ── */
  const isDeadlinePassed = () => {
    if (!submissionDeadline) return false;
    return new Date() > new Date(submissionDeadline);
  };

  const canSubmit = () => isDeadlinePassed() ? allowLateSubmissions : true;

  /* ── file picking ── */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (!picked.length) return;

    const existingCount  = existingSubmission?.files?.length ?? 0;
    const currentCount   = attachedFiles.length;
    const totalCurrent   = existingCount + currentCount;

    if (totalCurrent >= 5) {
      toast.current?.show({ severity: 'warn', summary: 'Limit Reached', detail: 'You can attach up to 5 files in total.', life: 3000 });
      return;
    }

    let totalSize = attachedFiles.reduce((s, f) => s + f.size, 0);
    if (existingSubmission?.files) {
      totalSize += existingSubmission.files.reduce((s, f) => s + f.size, 0);
    }

    const newFiles: AttachedFile[] = [];

    for (const file of picked) {
      if (file.size > 3 * 1024 * 1024) {
        toast.current?.show({ severity: 'warn', summary: 'File Too Large', detail: `${file.name} exceeds 3 MB.`, life: 3000 });
        continue;
      }
      if (totalSize + file.size > 10 * 1024 * 1024) {
        toast.current?.show({ severity: 'warn', summary: 'Total Size Exceeded', detail: 'Total file size cannot exceed 10 MB.', life: 3000 });
        break;
      }
      if (totalCurrent + newFiles.length >= 5) {
        toast.current?.show({ severity: 'warn', summary: 'Limit Exceeded', detail: 'You can attach up to 5 files in total.', life: 3000 });
        break;
      }
      newFiles.push({ id: Math.random().toString(36).slice(2, 9), name: file.name, size: file.size, type: file.type, file });
      totalSize += file.size;
    }

    setAttachedFiles(prev => [...prev, ...newFiles]);
    // reset input so the same file can be re-picked after removal
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (id: string) => setAttachedFiles(prev => prev.filter(f => f.id !== id));

  /* ── submit ── */
  const handleSubmit = async () => {
    setConfirmOpen(false);
    if (!submissionText.trim() && attachedFiles.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'Empty Submission', detail: 'Add text or attach files before submitting.', life: 3000 });
      return;
    }
    if (!canSubmit()) {
      toast.current?.show({ severity: 'error', summary: 'Deadline Passed', detail: 'Late submissions are not allowed for this assignment.', life: 4000 });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('assignment_id', assignmentId);
      formData.append('submission_text', submissionText);
      attachedFiles.forEach(f => formData.append('files[]', f.file, f.name));

      let submissionId: string;

      if (existingSubmission) {
        await apiClient.put(`/v1/assignment-submissions/${existingSubmission.submission_id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        submissionId = existingSubmission.submission_id;
      } else {
        const createRes = await apiClient.post('/v1/assignment-submissions', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        submissionId = createRes.data.data.submission_id;
      }

      await apiClient.post(`/v1/assignment-submissions/${submissionId}/submit`);

      toast.current?.show({ severity: 'success', summary: 'Submitted!', detail: 'Assignment submitted successfully.', life: 3500 });
      setAttachedFiles([]);
      setSubmissionText('');
      onSubmissionUpdate();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Submission Failed',
        detail: err.response?.data?.error || 'Failed to submit assignment. Please try again.',
        life: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ═══════════════════════════════════════════════════════════════ */
  /* VIEW: already submitted / graded                               */
  /* ═══════════════════════════════════════════════════════════════ */
  if (existingSubmission && (existingSubmission.status === 'submitted' || existingSubmission.status === 'graded')) {
    const isGraded = existingSubmission.status === 'graded';

    return (
      <>
        <style>{`
          .ssu-file-item:hover { background: #f0f4ff !important; border-color: #bfdbfe !important; }
        `}</style>

        <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 1px 6px rgba(14,34,93,0.07)' }}>

          {/* Top accent */}
          <div style={{ height: '3px', background: isGraded ? `linear-gradient(90deg, ${C.gold} 0%, ${C.amber} 100%)` : `linear-gradient(90deg, ${C.green} 0%, #22c55e 100%)` }} />

          <div style={{ padding: '18px 20px' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', paddingBottom: '14px', borderBottom: `1px solid ${C.border}`, flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '42px', height: '42px', borderRadius: '21px', background: isGraded ? C.goldDim : C.greenDim, border: `2px solid ${isGraded ? C.gold : C.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={`pi ${isGraded ? 'pi-check-circle' : 'pi-check'}`} style={{ fontSize: '1.1rem', color: isGraded ? C.gold : C.green }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Your Submission</div>
                  <div style={{ fontSize: '0.75rem', color: C.sub, marginTop: '2px' }}>
                    {isGraded ? 'Graded' : 'Submitted'}
                    {existingSubmission.is_late && ' · Late'}
                  </div>
                </div>
              </div>

              {/* Grade badge */}
              {isGraded && (
                <div style={{ background: '#FFFBEB', border: `2px solid ${C.gold}`, borderRadius: '12px', padding: '10px 18px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Score</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: '800', color: C.navy, lineHeight: 1 }}>
                    {existingSubmission.marks_obtained}<span style={{ fontSize: '0.9rem', color: C.sub }}>/{existingSubmission.total_marks}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Submission text */}
            {existingSubmission.submission_text && (
              <div style={{ marginBottom: '16px' }}>
                <SectionLabel>Your Answer</SectionLabel>
                <div style={{ background: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px' }}>
                  <p style={{ margin: 0, fontSize: '0.86rem', color: C.body, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {existingSubmission.submission_text}
                  </p>
                </div>
              </div>
            )}

            {/* Attached files */}
            {existingSubmission.files?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <SectionLabel>Attached Files ({existingSubmission.files.length})</SectionLabel>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {existingSubmission.files.map((file, i) => (
                    <a key={i} href={file.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      <div
                        className="ssu-file-item"
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s' }}
                      >
                        <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#E8EEF7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className="pi pi-file" style={{ fontSize: '0.9rem', color: C.navy }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.83rem', fontWeight: '600', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.file_name}</div>
                          <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: '2px' }}>{formatFileSize(file.size)}</div>
                        </div>
                        <i className="pi pi-download" style={{ fontSize: '0.9rem', color: C.gold }} />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div style={{ paddingTop: '12px', borderTop: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className="pi pi-clock" style={{ fontSize: '0.78rem', color: C.muted }} />
                <span style={{ fontSize: '0.78rem', color: C.sub }}>
                  Submitted: {formatDateTime(existingSubmission.submitted_at)}
                </span>
              </div>
              {existingSubmission.is_late && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 10px', background: C.amberDim, border: `1px solid ${C.amberBorder}`, borderRadius: '8px', width: 'fit-content' }}>
                  <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.75rem', color: C.amber }} />
                  <span style={{ fontSize: '0.78rem', fontWeight: '600', color: C.amber }}>Late submission</span>
                </div>
              )}
            </div>

            {/* Grading details */}
            {isGraded && (
              <div style={{ marginTop: '18px', background: '#FFFBEB', borderRadius: '14px', border: `2px solid ${C.gold}`, padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '14px' }}>
                  <i className="pi pi-star-fill" style={{ fontSize: '0.95rem', color: C.gold }} />
                  <span style={{ fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Evaluation</span>
                </div>

                {/* Score row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.white, borderRadius: '10px', padding: '10px 14px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '600', color: C.navy }}>Marks Obtained</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: '800', color: C.navy }}>
                    {existingSubmission.marks_obtained}
                    <span style={{ fontSize: '0.85rem', fontWeight: '500', color: C.sub }}> / {existingSubmission.total_marks}</span>
                  </span>
                </div>

                {/* Feedback */}
                {existingSubmission.feedback && (
                  <div style={{ marginBottom: '12px' }}>
                    <SectionLabel>Feedback</SectionLabel>
                    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '10px 12px' }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: C.body, lineHeight: 1.65 }}>
                        {existingSubmission.feedback}
                      </p>
                    </div>
                  </div>
                )}

                {/* Reviewer */}
                {existingSubmission.reviewed_by_name && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="pi pi-user" style={{ fontSize: '0.75rem', color: C.sub }} />
                    <span style={{ fontSize: '0.78rem', color: C.sub }}>
                      Reviewed by: {existingSubmission.reviewed_by_name}
                      {existingSubmission.reviewed_at && ` · ${formatDateTime(existingSubmission.reviewed_at)}`}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  /* ═══════════════════════════════════════════════════════════════ */
  /* VIEW: upload / draft                                           */
  /* ═══════════════════════════════════════════════════════════════ */
  const submittable = canSubmit();
  const isLate      = isDeadlinePassed() && allowLateSubmissions;

  return (
    <>
      <style>{`
        .ssu-textarea:focus { outline: none; border-color: ${C.navy} !important; box-shadow: 0 0 0 3px rgba(14,34,93,0.08); }
        .ssu-attach-btn:hover { background: ${C.navyHover} !important; border-color: rgba(14,34,93,0.25) !important; color: ${C.navy} !important; }
        .ssu-submit-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(14,34,93,0.25); }
        .ssu-remove-btn:hover { background: ${C.redDim} !important; border-color: ${C.redBorder} !important; }
      `}</style>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Confirm modal */}
      {confirmOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
          <div style={{ background: C.white, borderRadius: '18px', padding: '28px', maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: C.goldDim, border: `2px solid ${C.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
              <i className="pi pi-send" style={{ fontSize: '1.3rem', color: C.navy }} />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '700', color: C.navy }}>Submit Assignment?</h3>
            <p style={{ margin: '0 0 6px 0', fontSize: '0.86rem', color: C.sub, lineHeight: 1.55 }}>
              Are you sure you want to submit? This action cannot be undone.
            </p>
            {isLate && (
              <div style={{ display: 'flex', gap: '8px', padding: '10px 12px', background: C.amberDim, border: `1px solid ${C.amberBorder}`, borderRadius: '10px', marginBottom: '8px' }}>
                <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.88rem', color: C.amber, flexShrink: 0, marginTop: '1px' }} />
                <span style={{ fontSize: '0.82rem', color: C.amber, fontWeight: '600' }}>This will be marked as a late submission.</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setConfirmOpen(false)}
                style={{ flex: 1, padding: '11px', background: '#f3f4f6', border: 'none', borderRadius: '10px', fontSize: '0.88rem', fontWeight: '600', color: C.body, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                style={{ flex: 1, padding: '11px', background: C.navy, border: `2px solid ${C.gold}`, borderRadius: '10px', fontSize: '0.88rem', fontWeight: '700', color: C.white, cursor: 'pointer' }}
              >
                Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 1px 6px rgba(14,34,93,0.07)' }}>

        {/* Top accent */}
        <div style={{ height: '3px', background: `linear-gradient(90deg, ${C.blue} 0%, #60a5fa 100%)` }} />

        <div style={{ padding: '18px 20px' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px', paddingBottom: '14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '21px', background: C.blueDim, border: `2px solid ${C.blueBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="pi pi-pencil" style={{ fontSize: '1.1rem', color: C.blue }} />
            </div>
            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: '700', color: C.navy }}>Submit Assignment</div>
              <div style={{ fontSize: '0.75rem', color: submittable ? C.sub : C.red, marginTop: '2px' }}>
                {submittable ? (isLate ? 'Deadline passed — late submission allowed' : 'Complete and submit your work') : 'Deadline passed — submissions closed'}
              </div>
            </div>
          </div>

          {/* Deadline passed banner */}
          {!submittable && (
            <div style={{ display: 'flex', gap: '10px', padding: '12px 14px', background: C.redDim, border: `1px solid ${C.redBorder}`, borderLeft: `3px solid ${C.red}`, borderRadius: '10px', marginBottom: '18px' }}>
              <i className="pi pi-ban" style={{ fontSize: '1rem', color: C.red, flexShrink: 0, marginTop: '1px' }} />
              <span style={{ fontSize: '0.84rem', fontWeight: '600', color: C.red }}>
                Submission deadline has passed and late submissions are not allowed.
              </span>
            </div>
          )}

          {/* Late warning banner */}
          {submittable && isLate && (
            <div style={{ display: 'flex', gap: '10px', padding: '10px 14px', background: C.amberDim, border: `1px solid ${C.amberBorder}`, borderLeft: `3px solid ${C.amber}`, borderRadius: '10px', marginBottom: '18px' }}>
              <i className="pi pi-clock" style={{ fontSize: '0.95rem', color: C.amber, flexShrink: 0, marginTop: '1px' }} />
              <span style={{ fontSize: '0.83rem', fontWeight: '600', color: C.amber }}>
                Deadline has passed. Your submission will be marked as late.
              </span>
            </div>
          )}

          {/* Text input */}
          <div style={{ marginBottom: '16px' }}>
            <SectionLabel>Your Answer</SectionLabel>
            <textarea
              className="ssu-textarea"
              value={submissionText}
              onChange={e => setSubmissionText(e.target.value)}
              disabled={!submittable}
              placeholder="Type your submission here…"
              rows={6}
              style={{
                width:        '100%',
                padding:      '12px 14px',
                border:       `1px solid ${C.border}`,
                borderRadius: '10px',
                fontSize:     '0.86rem',
                color:        C.body,
                background:   submittable ? C.white : '#f9fafb',
                resize:       'vertical',
                fontFamily:   'inherit',
                lineHeight:   1.65,
                boxSizing:    'border-box',
                transition:   'border-color 0.15s, box-shadow 0.15s',
              }}
            />
          </div>

          {/* Attach files button */}
          {submittable && (
            <div style={{ marginBottom: '16px' }}>
              <button
                className="ssu-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                style={{ width: '100%', padding: '10px', border: `1.5px dashed ${C.border}`, borderRadius: '10px', background: 'transparent', fontSize: '0.85rem', fontWeight: '600', color: C.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.15s' }}
              >
                <i className="pi pi-cloud-upload" style={{ fontSize: '1rem' }} />
                Attach Files
              </button>

              {/* File info note */}
              <div style={{ display: 'flex', gap: '8px', padding: '9px 12px', background: '#FEF3C7', border: '1px solid rgba(245,158,11,0.3)', borderLeft: `3px solid ${C.amber}`, borderRadius: '8px', marginTop: '10px' }}>
                <i className="pi pi-info-circle" style={{ fontSize: '0.85rem', color: C.amber, flexShrink: 0, marginTop: '1px' }} />
                <span style={{ fontSize: '0.78rem', color: '#92400e', lineHeight: 1.5 }}>
                  Max 5 files · 10 MB total · Formats: PDF, Excel, Word, Images, ZIP, RAR
                </span>
              </div>
            </div>
          )}

          {/* Newly attached files */}
          {attachedFiles.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <SectionLabel>Files to Upload ({attachedFiles.length})</SectionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {attachedFiles.map(file => (
                  <div
                    key={file.id}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: C.blueDim, border: `1px solid ${C.blueBorder}`, borderRadius: '10px' }}
                  >
                    <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="pi pi-file" style={{ fontSize: '0.88rem', color: C.navy }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.83rem', fontWeight: '600', color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</div>
                      <div style={{ fontSize: '0.72rem', color: C.sub, marginTop: '2px' }}>{formatFileSize(file.size)}</div>
                    </div>
                    <button
                      className="ssu-remove-btn"
                      onClick={() => removeFile(file.id)}
                      style={{ width: '28px', height: '28px', borderRadius: '7px', background: 'transparent', border: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.red, transition: 'all 0.15s', flexShrink: 0 }}
                    >
                      <i className="pi pi-times" style={{ fontSize: '0.72rem' }} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submit button */}
          {submittable && (
            <button
              className="ssu-submit-btn"
              onClick={() => setConfirmOpen(true)}
              disabled={isSubmitting}
              style={{
                width:          '100%',
                padding:        '13px',
                background:     isSubmitting ? C.muted : C.navy,
                border:         `2px solid ${isSubmitting ? 'transparent' : C.gold}`,
                borderRadius:   '12px',
                fontSize:       '0.9rem',
                fontWeight:     '700',
                color:          C.white,
                cursor:         isSubmitting ? 'not-allowed' : 'pointer',
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                gap:            '8px',
                transition:     'all 0.18s ease',
                marginTop:      '4px',
              }}
            >
              {isSubmitting ? (
                <>
                  <ProgressSpinner style={{ width: '18px', height: '18px' }} strokeWidth="4" animationDuration=".7s" />
                  Submitting…
                </>
              ) : (
                <>
                  <i className="pi pi-send" style={{ fontSize: '0.88rem' }} />
                  Submit Assignment
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default StudentSubmissionUpload;